import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Modal,
  ModalDialog,
  Option,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";

import { DarshanMedia, DarshanType, fetchDarshanByType } from "../api";
import JoyDatePicker from "../components/common/JoyDatePicker";
import PageHero from "../components/common/PageHero";

const TYPES: DarshanType[] = ['Sringar', 'Mangala', 'Festival'];

export default function DarshanGalleryDay() {
  const params = useParams();
  const navigate = useNavigate();
  const [type, setType] = useState<DarshanType>((params.type as DarshanType) || 'Sringar');
  const [date, setDate] = useState<Dayjs>(params.date ? dayjs(params.date) : dayjs());
  const [media, setMedia] = useState<DarshanMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number>(0);
  const isoDate = useMemo(() => date.format("YYYY-MM-DD"), [date]);
  const heroDate = useMemo(() => date.format("D MMMM YYYY"), [date]);
  const dayName = useMemo(() => date.format("dddd"), [date]);
  const mediaCountLabel = media.length === 1 ? "darshan moment" : "darshan moments";
  const touchStartX = useRef(0);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchDarshanByType(type, { date: isoDate });
      setMedia(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load darshan", type, isoDate, error);
      setMedia([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    navigate(`/darshan-gallery/${type}/${isoDate}`, { replace: true });
  }, [type, isoDate, navigate]);

  const prevDay = () => setDate((d) => d.subtract(1, "day"));
  const nextDay = () => setDate((d) => d.add(1, "day"));
  const canNext = useMemo(() => date.isBefore(dayjs(), "day"), [date]);
  const canPrev = useMemo(() => dayjs().diff(date, "day") < 45, [date]);

  useEffect(() => {
    const preload = (idx: number) => {
      const item = media[idx];
      if (item && item.mediaType === "image") {
        const img = new Image();
        img.src = item.url;
      }
    };

    preload(viewerIndex + 1);
    preload(viewerIndex - 1);
  }, [viewerIndex, media]);

  useEffect(() => {
    if (!viewerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setViewerOpen(false);
      if (event.key === "ArrowLeft") setViewerIndex((index) => Math.max(0, index - 1));
      if (event.key === "ArrowRight") setViewerIndex((index) => Math.min(media.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerOpen, media.length]);

  return (
    <Box>
      <PageHero
        eyebrow="Daily darshan archive"
        title={`${type} darshan • ${heroDate}`}
        description={`Relish the seva captured on ${dayName}. Scroll through today's gallery, then steward fresh uploads for devotees across the world.`}
        actions={
          <>
            <Button
              component={RouterLink}
              to="/darshan"
              variant="soft"
              color="neutral"
              startDecorator={<UploadRoundedIcon />}
            >
              Steward new darshan
            </Button>
            <Button
              component={RouterLink}
              to="/darshan-gallery"
              variant="plain"
              color="neutral"
              startDecorator={<CollectionsRoundedIcon />}
              sx={{ color: "rgba(255,255,255,0.86)" }}
            >
              Browse full timeline
            </Button>
          </>
        }
      />

      <Stack spacing={3}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: "xl",
            p: { xs: 2.5, md: 3 },
            gap: 2,
          }}
        >
          <Stack spacing={0.75}>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Curate darshan focus
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 540 }}>
              Select a darshan type and date to revisit seva highlights. Use the arrows to move through nearby days captured by the photography team.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
          >
            <Stack spacing={0.5} sx={{ flex: { lg: 1 } }}>
              <Typography level="body-xs" sx={{ textTransform: "uppercase", letterSpacing: 0.8, color: "text.tertiary" }}>
                Darshan category
              </Typography>
              <Select
                value={type}
                onChange={(_, value) => setType((value as DarshanType) ?? "Sringar")}
                size="md"
                sx={{ minWidth: { xs: "100%", sm: 220 } }}
                aria-label="Select darshan type"
              >
                {TYPES.map((entry) => (
                  <Option key={entry} value={entry}>
                    {entry} darshan
                  </Option>
                ))}
              </Select>
            </Stack>

            <Stack spacing={0.5} sx={{ flex: { lg: 1 } }}>
              <JoyDatePicker
                label="Choose a date"
                value={date}
                format="DD MMM YYYY"
                onChange={(newDate) => {
                  if (newDate) {
                    setDate(newDate);
                  }
                }}
                slotProps={{
                  textField: {
                    placeholder: "Select a date",
                  },
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <IconButton size="sm" variant="outlined" onClick={prevDay} disabled={!canPrev}>
                <ArrowBackIosNewRoundedIcon />
              </IconButton>
              <IconButton size="sm" variant="outlined" onClick={nextDay} disabled={!canNext}>
                <ArrowForwardIosRoundedIcon />
              </IconButton>
              <Button size="sm" variant="soft" color="neutral" onClick={() => setDate(dayjs())}>
                Jump to today
              </Button>
            </Stack>
          </Stack>
        </Card>

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid key={index} xs={12} sm={6} lg={4}>
                <Card variant="outlined" sx={{ borderRadius: "xl", p: 0, overflow: "hidden" }}>
                  <Skeleton variant="rectangular" sx={{ height: 220 }} />
                  <Stack spacing={1} sx={{ p: 2 }}>
                    <Skeleton width="60%" />
                    <Skeleton width="40%" />
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : media.length === 0 ? (
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              textAlign: "center",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              startDecorator={<CalendarMonthRoundedIcon fontSize="small" />}
            >
              Awaiting uploads
            </Chip>
            <Typography level="title-sm" sx={{ fontWeight: 600 }}>
              No {type.toLowerCase()} darshan captured on {heroDate}.
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
              Invite photographers to share today’s darshan so devotees around the world can relish the Lord’s beauty together.
            </Typography>
            <Button
              size="sm"
              variant="soft"
              color="primary"
              component={RouterLink}
              to="/darshan"
              startDecorator={<UploadRoundedIcon />}
            >
              Open upload workspace
            </Button>
          </Card>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Chip size="sm" variant="soft" color="primary">
                {media.length} {mediaCountLabel}
              </Chip>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Captured on {dayName}, {heroDate}
              </Typography>
            </Stack>
            <Box
              sx={{
                columnCount: { xs: 1, sm: 2, lg: 3 },
                columnGap: 20,
              }}
            >
              {media.map((item, index) => (
                <Card
                  key={item.id}
                  variant="outlined"
                  onClick={() => {
                    setViewerIndex(index);
                    setViewerOpen(true);
                  }}
                  sx={{
                    borderRadius: "xl",
                    mb: 2,
                    breakInside: "avoid",
                    cursor: "zoom-in",
                    overflow: "hidden",
                    transition: "transform 0.18s ease, box-shadow 0.18s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "md",
                    },
                  }}
                >
                  {item.mediaType === "image" ? (
                    <Box
                      component="img"
                      loading="lazy"
                      src={item.url}
                      alt={item.title || "Darshan"}
                      sx={{ width: "100%", display: "block" }}
                    />
                  ) : (
                    <Box
                      component="video"
                      src={item.url}
                      controls
                      sx={{ width: "100%", display: "block" }}
                    />
                  )}
                  <Stack spacing={0.5} sx={{ p: 2 }}>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      {item.title || `${type} darshan glimpse`}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      Tap to open the full lightbox
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Box>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Tip: Use your keyboard arrows or swipe to move between darshan highlights.
            </Typography>
          </Stack>
        )}
      </Stack>

      <Modal open={viewerOpen} onClose={() => setViewerOpen(false)}>
        <ModalDialog layout="fullscreen" variant="plain" sx={{ p: 0, bgcolor: "rgba(7,10,18,0.96)" }}>
          <IconButton
            size="md"
            variant="soft"
            color="neutral"
            onClick={() => setViewerOpen(false)}
            sx={{ position: "fixed", top: 16, right: 16, zIndex: 2 }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <IconButton
            size="md"
            variant="soft"
            color="neutral"
            onClick={() => setViewerIndex((index) => Math.max(0, index - 1))}
            disabled={viewerIndex <= 0}
            sx={{ position: "fixed", top: "50%", left: 16, transform: "translateY(-50%)", zIndex: 2 }}
          >
            <ChevronLeftRounded />
          </IconButton>
          <IconButton
            size="md"
            variant="soft"
            color="neutral"
            onClick={() => setViewerIndex((index) => Math.min(media.length - 1, index + 1))}
            disabled={viewerIndex >= media.length - 1}
            sx={{ position: "fixed", top: "50%", right: 16, transform: "translateY(-50%)", zIndex: 2 }}
          >
            <ChevronRightRounded />
          </IconButton>
          <Box
            sx={{
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, md: 4 },
            }}
            onTouchStart={(event) => {
              touchStartX.current = event.touches[0]?.clientX ?? 0;
            }}
            onTouchEnd={(event) => {
              const delta = (event.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
              if (delta > 40) {
                setViewerIndex((index) => Math.max(0, index - 1));
              }
              if (delta < -40) {
                setViewerIndex((index) => Math.min(media.length - 1, index + 1));
              }
            }}
          >
            {media[viewerIndex]?.mediaType === "image" ? (
              <Box
                component="img"
                src={media[viewerIndex]?.url}
                alt={media[viewerIndex]?.title || "Darshan"}
                sx={{ maxHeight: "88vh", maxWidth: "100%", borderRadius: "lg", boxShadow: "xl" }}
              />
            ) : (
              <Box
                component="video"
                src={media[viewerIndex]?.url}
                controls
                sx={{ maxHeight: "88vh", maxWidth: "100%", borderRadius: "lg", boxShadow: "xl" }}
              />
            )}
          </Box>
          <Stack
            spacing={0.5}
            sx={{ position: "fixed", bottom: 24, left: 24, color: "rgba(255,255,255,0.92)" }}
          >
            {media[viewerIndex]?.title ? (
              <Typography level="title-sm" sx={{ color: "inherit" }}>
                {media[viewerIndex]?.title}
              </Typography>
            ) : null}
            <Typography level="body-xs" sx={{ color: "rgba(255,255,255,0.78)" }}>
              Captured on {dayName}, {heroDate}
            </Typography>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
